import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Computer, SmartphoneDevice, Trash, ShieldAlert } from 'iconoir-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Parse a User-Agent string into a friendly "Browser on OS" label.
 * Lightweight and intentionally not exhaustive — we only need it to help
 * the user recognize their own devices, not to fingerprint. */
function parseUA(ua) {
  if (!ua) return { label: 'Unknown device', isMobile: false };
  const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);

  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';

  let os = '';
  if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'Mac';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';

  return { label: os ? `${browser} on ${os}` : browser, isMobile };
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diffSec = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function DeviceSessionsCard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/sessions`, { withCredentials: true });
      setSessions(data?.items || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const revokeOne = async (id) => {
    setRevoking(id);
    try {
      await axios.delete(`${API}/sessions/${id}`, { withCredentials: true });
      toast.success('Device signed out');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to sign out device');
    } finally {
      setRevoking(null);
    }
  };

  const signOutOthers = async () => {
    try {
      const { data } = await axios.post(
        `${API}/sessions/sign-out-others`, {}, { withCredentials: true },
      );
      const n = data?.revoked || 0;
      toast.success(n ? `Signed out of ${n} other device${n === 1 ? '' : 's'}` : 'No other devices to sign out');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to sign out other devices');
    }
  };

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <Card className="card-surface p-6" data-testid="device-sessions-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="font-serif text-xl text-charcoal">Devices &amp; Sessions</h2>
          <p className="text-xs text-muted mt-1" data-testid="device-sessions-count">
            {loading ? 'Loading…' : `${sessions.length} active session${sessions.length === 1 ? '' : 's'}`}
          </p>
        </div>
        {otherCount > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                className="btn-secondary text-xs flex items-center gap-2"
                data-testid="sign-out-others-btn"
              >
                <ShieldAlert className="w-4 h-4" strokeWidth={1.5} />
                Sign out everywhere else
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Sign out other devices?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will end {otherCount} other session{otherCount === 1 ? '' : 's'}.
                  Anyone using this account on another browser, phone, or tablet
                  will be signed out immediately and need to log in again. Your
                  current device stays signed in.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={signOutOthers}
                  className="bg-terracotta text-white hover:bg-terracotta-dark"
                  data-testid="confirm-sign-out-others-btn"
                >
                  Sign out {otherCount} device{otherCount === 1 ? '' : 's'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <p className="text-sm text-muted mb-4">
        These are the devices currently signed into your account. If you don't
        recognize one, sign it out right away.
      </p>

      {!loading && sessions.length === 0 && (
        <p className="text-muted text-sm py-2" data-testid="device-sessions-empty">
          No active sessions found.
        </p>
      )}

      <div className="space-y-2">
        {sessions.map((s) => {
          const { label, isMobile } = parseUA(s.user_agent);
          const Icon = isMobile ? SmartphoneDevice : Computer;
          return (
            <div
              key={s.id}
              className="flex items-center gap-3 p-3 bg-page rounded-sm"
              data-testid={`device-session-row-${s.id}`}
            >
              <div className="shrink-0 w-9 h-9 rounded-sm bg-charcoal/5 flex items-center justify-center text-charcoal">
                <Icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-charcoal text-sm font-medium truncate">{label}</span>
                  {s.is_current && (
                    <Badge className="bg-moss/10 text-moss border text-[10px] uppercase tracking-wider"
                      data-testid={`device-current-badge-${s.id}`}>
                      This device
                    </Badge>
                  )}
                  {s.remember && !s.is_current && (
                    <Badge className="bg-ochre/10 text-ochre border text-[10px] uppercase tracking-wider">
                      Remembered
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted mt-0.5">
                  {s.ip || 'Unknown IP'} · Last active {relativeTime(s.last_seen)}
                </p>
              </div>
              {!s.is_current && (
                <button
                  onClick={() => revokeOne(s.id)}
                  disabled={revoking === s.id}
                  className="p-1.5 text-muted hover:text-terracotta transition-colors disabled:opacity-50"
                  data-testid={`revoke-session-btn-${s.id}`}
                  title="Sign out this device"
                >
                  <Trash className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
